import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Form, Input, Space } from "antd";
import { useState } from "react";
import { useCreateReport } from "@/common/artemis-operations/reports";
import {
	ERROR_REPORT_CREATION_DESC,
	ERROR_REPORT_CREATION_TITLE,
	REPORT_CREATED_DESC,
	REPORT_CREATED_TITLE,
} from "@/common/messages";
import { ReportType } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";

interface ReportCreateWizardProps {
	open: boolean;
	onClose: () => void;
}

const REPORT_TYPE_OPTIONS = [
	{
		value: ReportType.GENERIC,
		label: "Generic",
		description: "A freeform knowledge artifact or note.",
	},
	{
		value: ReportType.INCIDENT,
		label: "Incident",
		description: "A production issue or service disruption.",
	},
	{
		value: ReportType.INVESTIGATION,
		label: "Investigation",
		description: "An open-ended analysis or research thread.",
	},
];

/** Wizard for creating a new report. */
export function ReportCreateWizard({ open, onClose }: ReportCreateWizardProps) {
	const { workspaceId, orgSlug } = useParams({ strict: false }) as {
		workspaceId: string;
		orgSlug: string;
	};
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const queryClient = useQueryClient();
	const { mutate: createReport } = useCreateReport();
	const navigate = useNavigate();
	const notify = useNotify();

	const reset = () => {
		form.resetFields();
		setLoading(false);
	};

	const handleComplete = () => {
		form.validateFields().then((values) => {
			setLoading(true);
			createReport(
				{
					workspaceId,
					title: values.title,
					type: values.type ?? ReportType.GENERIC,
				},
				{
					onSuccess: (data) => {
						notify.success(REPORT_CREATED_TITLE, REPORT_CREATED_DESC);
						queryClient.invalidateQueries({
							queryKey: ["reports", workspaceId],
						});
						onClose();
						reset();
						const id = (data.createReport.report as { id: string } | null)?.id;
						if (id) {
							navigate({
								to: "/$orgSlug/workspaces/$workspaceId/reports/$reportId",
								params: { orgSlug, workspaceId, reportId: id },
							});
						}
					},
					onError: () => {
						setLoading(false);
						notify.error(
							ERROR_REPORT_CREATION_TITLE,
							ERROR_REPORT_CREATION_DESC,
						);
					},
				},
			);
		});
	};

	return (
		<Wizard
			open={open}
			title="Create Report"
			completeLabel="Create"
			form={form}
			loading={loading}
			onComplete={handleComplete}
			onCancel={() => {
				reset();
				onClose();
			}}
			pages={[
				{
					id: "details",
					title: "Details",
					content: (
						<Space
							orientation="vertical"
							size="small"
							style={{ width: "100%" }}
						>
							<Form.Item
								name="type"
								label="Type"
								initialValue={ReportType.GENERIC}
								rules={[{ required: true }]}
							>
								<SegmentedSelection options={REPORT_TYPE_OPTIONS} />
							</Form.Item>
							<Form.Item
								name="title"
								label="Title"
								rules={[{ required: true }]}
							>
								<Input placeholder="Report title" />
							</Form.Item>
						</Space>
					),
				},
			]}
		/>
	);
}
